/**
 * /auth/google/confirm — the screen Google sign-in ALWAYS routes through for
 * a brand-new identity, before any account is created. useAuth.ts#googlePreview
 * verifies the ID token and, for a first-time Google user, returns a preview
 * of what Google gave us (email, firstName, lastName?) plus a short-lived
 * googleTicket — no User document exists yet at that point. This page shows
 * that data pre-filled and editable, and only on explicit submit does
 * useAuth.ts#completeGoogleSignIn actually create the account. Nothing from
 * Google is ever used without the person reviewing and confirming it here
 * first — a known/returning Google identity skips this page entirely (see
 * googlePreview's isNewIdentity: false branch), since nothing new is being
 * asserted about them in that case.
 *
 * PUBLIC route (see App.tsx) — reached before any session/tokens exist, so
 * it cannot sit behind ProtectedRoute. The googleTicket lives only in router
 * state (never the URL/history), same reasoning as Signup's signupTicket.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, User, Mail } from 'lucide-react';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from '../home/marketing-shell';
import { useAuth } from '@/hooks/useAuth';

interface ConfirmState {
  googleTicket: string;
  preview: { email: string; firstName: string; lastName?: string };
}

function GoogleConfirmForm({ googleTicket, preview }: ConfirmState) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { completeGoogleSignIn, loading } = useAuth();

  const [firstName, setFirstName] = useState(preview.firstName || '');
  const [lastName, setLastName] = useState(preview.lastName || '');
  const [errors, setErrors] = useState<Partial<Record<'firstName', string>>>({});
  const [submitError, setSubmitError] = useState('');

  function validate(): boolean {
    const next: typeof errors = {};
    if (!firstName.trim()) next.firstName = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const result = await completeGoogleSignIn({
      googleTicket,
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
    });
    if (!result.success) {
      const message = result.error || 'Could not create your account';
      setSubmitError(message);
      // Unrecoverable on THIS screen — the ticket is tied to one Google
      // identity, there's no "try a different email" retry here — so send
      // them back to start over.
      if (/expired|invalid/i.test(message)) {
        setTimeout(() => navigate('/signup', { replace: true }), 3000);
      }
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px 11px 40px', borderRadius: 10, background: t.muted,
    border: `1px solid ${t.border}`, color: t.fg, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none',
  };
  const readOnlyInputStyle: React.CSSProperties = { ...inputStyle, color: t.mutedFg, cursor: 'not-allowed' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: t.mutedFg, marginBottom: 6 };
  const errorStyle: React.CSSProperties = { fontSize: 11.5, color: '#ef4444', marginTop: 4 };
  const iconStyle: React.CSSProperties = { position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' };
  const focusHandlers = {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = t.green; },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = t.border; },
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '40px 32px',
        display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 440,
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, background: t.greenDim, border: `1px solid ${t.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <User size={24} color={t.green} />
        </div>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg, marginBottom: 6 }}>
          Confirm your details
        </h1>
        <p style={{ color: t.mutedFg, fontSize: 14 }}>
          This is what Google shared with us — check it over before we create your account.
        </p>
      </div>

      <div>
        <label style={labelStyle}>Email</label>
        <div style={{ position: 'relative' }}>
          <Mail size={15} color={t.mutedFg} style={iconStyle} />
          <input style={readOnlyInputStyle} value={preview.email} readOnly disabled />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="google-confirm-grid-2">
        <div>
          <label style={labelStyle}>First name</label>
          <div style={{ position: 'relative' }}>
            <User size={15} color={t.mutedFg} style={iconStyle} />
            <input
              style={inputStyle}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              autoFocus
              {...focusHandlers}
            />
          </div>
          {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
        </div>
        <div>
          <label style={labelStyle}>Last name</label>
          <div style={{ position: 'relative' }}>
            <User size={15} color={t.mutedFg} style={iconStyle} />
            <input
              style={inputStyle}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              placeholder="Optional"
              {...focusHandlers}
            />
          </div>
        </div>
      </div>

      {submitError && (
        <div style={{ color: '#ef4444', fontSize: 13, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px' }}>
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '14px 24px', borderRadius: 12, border: 'none', cursor: loading ? 'default' : 'pointer',
          background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
          fontFamily: 'Manrope, sans-serif', opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? 'Creating account...' : 'Confirm and continue'} {!loading && <ArrowRight size={16} />}
      </button>
      <style>{`.google-confirm-grid-2 { @media (max-width: 420px) { grid-template-columns: 1fr !important; } }`}</style>
    </form>
  );
}

export default function GoogleConfirm() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as ConfirmState | null;

  // Reached directly (refresh, back button, bad link) with no ticket in
  // router state — nothing to confirm, so there's nowhere useful to render;
  // send them back to start a real sign-in. Navigating from an effect
  // (never during render itself) — a render-phase navigate() call is a
  // React anti-pattern: it works today only because router state changes
  // happen to trigger a re-render before paint, not because it's a
  // supported pattern, and every other "recover from a bad state" redirect
  // in this codebase (Onboarding.tsx, OnboardingPending.tsx) already goes
  // through an effect or an event handler, never inline render logic.
  useEffect(() => {
    if (!state?.googleTicket) {
      navigate('/signup', { replace: true });
    }
  }, [state, navigate]);

  if (!state?.googleTicket) {
    return null;
  }

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '130px 24px 80px' }}>
          <GoogleConfirmForm googleTicket={state.googleTicket} preview={state.preview} />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
